import { defineStore } from 'pinia';
import { ref } from 'vue';

import type {
  LocalRoleResultDTO,
  LocalRoleType,
} from '@nora-monorepo/nora-api-client';

import { useApiStore } from '#ui-shared/stores/api/api-store';
import { appLogger } from '#ui-shared/utils/logger/LoggerClass';

/**
 * This store keeps track of all available local roles.
 *
 * Since this data is used in various places, we fetch it once and store it here for easy access.
 */
export const useRoleStore = defineStore('roleStore', () => {
  const api = useApiStore();

  /**
   *   Represents all valid local roles in operations
   */
  const availableOperationRoles = ref<LocalRoleResultDTO[]>();
  const defaultOperationRole = ref<LocalRoleResultDTO | undefined>();
  const defaultAdminRole = ref<LocalRoleResultDTO | undefined>();

  const availableConversationRoles = ref<LocalRoleResultDTO[]>();

  const availableGroupRoles = ref<LocalRoleResultDTO[]>();

  async function bootstrap(): Promise<void> {
    availableOperationRoles.value = await fetchLocalRoles('OPERATION');
    defaultOperationRole.value = getDefaultOperationRole(availableOperationRoles.value);
    defaultAdminRole.value = getDefaultOperationAdminRole(availableOperationRoles.value);

    availableConversationRoles.value = await fetchLocalRoles('CONVERSATION');
    availableGroupRoles.value = await fetchLocalRoles('GROUP');
  }

  function getDefaultOperationAdminRole(roles: LocalRoleResultDTO[]): LocalRoleResultDTO | undefined {
    const defaultAdmin = roles.find(role => role.defaultAdmin);

    if (!defaultAdmin) {
      appLogger.error({
        message: 'Unable to find default admin role for operations',
        context: {
          roles,
        },
      });
      return undefined;
    }
    return defaultAdmin;
  }

  function getDefaultOperationRole(roles: LocalRoleResultDTO[]): LocalRoleResultDTO | undefined {
    const defaultRole = roles.find(role => role.defaultUser);

    if (!defaultRole) {
      appLogger.error({
        message: 'Unable to find default role for operations',
        context: {
          roles,
        },
      });
      return undefined;
    }

    return defaultRole;
  }

  /**
   * Fetches the local roles of a given type.
   * @param type
   */
  async function fetchLocalRoles(type: LocalRoleType): Promise<LocalRoleResultDTO[]> {
    try {
      const result = await api.unwrap(
        api.nexusRole.listLocalRoles({
          type,
        }),
      );

      if (!result.data) {
        appLogger.error({
          message: `Unable to fetch local roles for ${type}`,
        });
      }

      return result.data ? result.data : [];
    }
    catch (error) {
      appLogger.error({
        message: `Unable to fetch local roles for ${type}`,
        sensitiveContext: {
          error,
        },
      });

      return [];
    }
  }

  return {
    availableOperationRoles,
    availableConversationRoles,
    availableGroupRoles,
    defaultOperationRole,
    defaultAdminRole,
    bootstrap,
  };
});
